/**
 * Linux Desktop Launcher Component
 * Allows users to launch new Linux EC2 desktops with NICE DCV
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Card, Button, Select, Typography, Space, message, Spin, Radio, InputNumber, Alert, Divider, Tooltip } from "antd";
import { LinuxOutlined, RocketOutlined, DesktopOutlined, SafetyOutlined, DatabaseOutlined, CameraOutlined, PlusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { launchLinuxDesktop } from "./store/linuxSlice";
import { getAvailableInstanceTypes } from "./api/linuxApi";
import { listSnapshots } from "../EBSSnapshots/api/ebsSnapshotApi";
import axiosInstance from "../../utils/axios";
import "./LinuxDesktopLauncher.scss";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// A single EBS volume cannot exceed 16 TiB, whatever the organisation's quota
// says — including an unlimited plan, whose quota is reported as a sentinel.
const EBS_MAX_VOLUME_GB = 16384;

function LinuxDesktopLauncher() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [instanceType, setInstanceType] = useState(null);
  const [persistent, setPersistent] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);
  const [networkPolicyRef, setNetworkPolicyRef] = useState(null);
  const [networkPolicies, setNetworkPolicies] = useState([]);
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [instanceTypesLoading, setInstanceTypesLoading] = useState(true);
  const [storageUnlimited, setStorageUnlimited] = useState(false);
  const [storageGB, setStorageGB] = useState(60);
  const [maxStorageGB, setMaxStorageGB] = useState(1000);

  // Snapshot restore source
  const [sourceMode, setSourceMode] = useState("fresh"); // "fresh" | "snapshot"
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  // Load Ready snapshots when user switches to snapshot mode
  useEffect(() => {
    if (sourceMode !== "snapshot") return;
    setSnapshotsLoading(true);
    listSnapshots()
      .then((data) => {
        const ready = (data.items || []).filter((s) => s.state === "Ready");
        setSnapshots(ready);
        if (ready.length > 0) setSelectedSnapshot(ready[0].name);
      })
      .catch(() => {})
      .finally(() => setSnapshotsLoading(false));
  }, [sourceMode]);

  // Load available instance types from the capacity API
  useEffect(() => {
    getAvailableInstanceTypes()
      .then((types) => {
        setInstanceTypes(types);
        // Pre-select the first available type
        const first = types.find((t) => t.available);
        if (first) setInstanceType(first.type);
      })
      .catch(() => {})
      .finally(() => setInstanceTypesLoading(false));

    axiosInstance
      .get("capacity/storage")
      .then((res) => {
        const { availableGB, unlimited } = res.data || {};
        setStorageUnlimited(Boolean(unlimited));
        // An unlimited plan reports a sentinel, not a quantity, so the ceiling
        // falls back to what a single EBS volume can actually be.
        const cap = unlimited ? EBS_MAX_VOLUME_GB : Math.floor(availableGB || 0);
        if (cap > 60) setMaxStorageGB(Math.min(cap, EBS_MAX_VOLUME_GB));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/network-policies?page=1&limit=100")
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        // Only show policies that apply to VMs
        const vmPolicies = items.filter(
          (p) => p.scope === "vms" || p.scope === "all"
        );
        setNetworkPolicies(vmPolicies);
      })
      .catch(() => {});
  }, []);

  const handleLaunch = async () => {
    if (sourceMode === "snapshot" && !selectedSnapshot) {
      message.error("Please select a snapshot to restore from.");
      return;
    }
    setIsLaunching(true);

    try {
      const params = {
        template: "linux-dcv",
        instanceType: instanceType,
        persistent: persistent
      };
      // Only set volumeSize for fresh desktops — snapshot volumes keep their original size.
      if (sourceMode === "fresh") {
        params.volumeSize = storageGB;
      }
      if (sourceMode === "snapshot") {
        params.restore = { fromSnapshot: selectedSnapshot };
      }
      if (networkPolicyRef) {
        params.networkPolicyRef = networkPolicyRef;
      }
      const result = await dispatch(launchLinuxDesktop(params)).unwrap();

      message.success("Linux desktop is being provisioned...");

      // Navigate to the viewer
      navigate(`/control/linux/${result.id}`);
    } catch (error) {
      console.error("Failed to create Linux desktop:", error);
      message.error(error?.message || "Failed to launch Linux desktop");
      setIsLaunching(false);
    }
  };


  // The summary panel and the description must describe the profile the user
  // actually selected. They were hardcoded to the GPU profile, which was
  // accidentally correct while the catalog offered nothing else.
  const selectedSpec = instanceTypes.find((t) => t.type === instanceType) || null;
  const hasGPU = (selectedSpec?.gpu ?? 0) > 0;
  const gpuLabel = !selectedSpec
    ? "—"
    : hasGPU
      ? `${selectedSpec.gpu > 1 ? `${selectedSpec.gpu} × ` : ""}NVIDIA T4`
      : "None — CPU rendering";

  return (
    <div className="linux-launcher-container">
      <Card className="launcher-card">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header */}
          <div className="launcher-header">
            <LinuxOutlined className="linux-icon" />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Linux Desktop
              </Title>
              <Text type="secondary">Launch a Linux EC2 instance with NICE DCV</Text>
            </div>
          </div>

          {/* Description */}
          <Paragraph>
            Launch a Linux desktop in the cloud
            {selectedSpec ? (hasGPU ? " with GPU acceleration" : ", CPU-rendered") : ""}. Your
            desktop will be accessible through NICE DCV for high-performance remote access.
          </Paragraph>

          {/* Source: fresh or restore from snapshot */}
          <div className="instance-selection">
            <Text strong>Source</Text>
            <Radio.Group
              value={sourceMode}
              onChange={(e) => {
                setSourceMode(e.target.value);
                setSelectedSnapshot(null);
              }}
              disabled={isLaunching}
              style={{ width: "100%", marginTop: 8 }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="fresh" style={{ width: "50%", textAlign: "center" }}>
                <PlusCircleOutlined /> Fresh Desktop
              </Radio.Button>
              <Radio.Button value="snapshot" style={{ width: "50%", textAlign: "center" }}>
                <CameraOutlined /> From Snapshot
              </Radio.Button>
            </Radio.Group>

            {sourceMode === "snapshot" && (
              <div style={{ marginTop: 12 }}>
                <Select
                  value={selectedSnapshot}
                  onChange={setSelectedSnapshot}
                  style={{ width: "100%" }}
                  size="large"
                  loading={snapshotsLoading}
                  disabled={isLaunching || snapshotsLoading}
                  placeholder={snapshotsLoading ? "Loading snapshots…" : "Select a snapshot"}
                  notFoundContent={
                    snapshotsLoading ? <Spin size="small" /> :
                    <Text type="secondary">No Ready snapshots found. Take a snapshot first.</Text>
                  }
                  optionLabelProp="label"
                >
                  {snapshots.map((s) => (
                    <Option key={s.name} value={s.name} label={s.name}>
                      <Space direction="vertical" size={0} style={{ width: "100%" }}>
                        <Text style={{ fontFamily: "monospace", fontSize: 12 }}>{s.name}</Text>
                        {s.desktopRef && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            from {s.desktopRef} · {s.volumeType}
                          </Text>
                        )}
                      </Space>
                    </Option>
                  ))}
                </Select>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 10 }}
                  message={
                    <span>
                      <strong>Timing:</strong> VM boots in ~2–3 min. Disk data is lazily restored by AWS in the background — the VM is fully usable immediately, but large volumes reach full I/O performance within 1–3 hours.
                    </span>
                  }
                />
              </div>
            )}

            {sourceMode === "fresh" && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                <Tooltip title="Taking a snapshot: 5–30 min depending on volume size (incremental after first). Creating a VM: ~2–3 min provisioning.">
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  Fresh desktop · ~2–3 min to boot
                </Tooltip>
              </Text>
            )}
          </div>

          <Divider style={{ margin: "4px 0" }} />

          {/* Instance Type Selection */}
          <div className="instance-selection">
            <Text strong>Instance Type</Text>
            <Select
              value={instanceType}
              onChange={setInstanceType}
              style={{ width: "100%" }}
              size="large"
              loading={instanceTypesLoading}
              disabled={isLaunching || instanceTypesLoading}
              placeholder="Select an instance type"
            >
              {instanceTypes.map((t) => (
                <Option key={t.type} value={t.type} disabled={!t.available}>
                  <Space style={{ opacity: t.available ? 1 : 0.45 }}>
                    <DesktopOutlined />
                    <span>
                      {t.type}
                      {" — "}
                      {t.family},{" "}
                      {t.vcpu} vCPU, {t.ram} GiB RAM
                      {t.gpu > 0 ? `, ${t.gpu} GPU` : ""}
                      {!t.available && (
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                          ({t.unavailableReason})
                        </Text>
                      )}
                    </span>
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          {/* Desktop Mode */}
          <div className="mode-selection">
            <Text strong>Desktop Mode</Text>
            <Radio.Group
              value={persistent}
              onChange={(e) => setPersistent(e.target.value)}
              disabled={isLaunching}
              style={{ width: "100%", marginTop: 8 }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={true} style={{ width: "50%", textAlign: "center" }}>
                Persistent
              </Radio.Button>
              <Radio.Button value={false} style={{ width: "50%", textAlign: "center" }}>
                Ephemeral
              </Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
              {persistent
                ? "You can pause your desktop and resume later. Storage is preserved."
                : "Desktop runs until you exit. Termination destroys all data."}
            </Text>
          </div>

          {/* Network Policy */}
          <div className="instance-selection">
            <Space>
              <SafetyOutlined />
              <Text strong>Traffic Filtering Policy</Text>
            </Space>
            <Select
              value={networkPolicyRef}
              onChange={setNetworkPolicyRef}
              style={{ width: "100%", marginTop: 8 }}
              size="large"
              disabled={isLaunching}
              allowClear
              placeholder="None (unrestricted egress)"
            >
              {networkPolicies.map((policy) => (
                <Option key={policy.name} value={policy.name}>
                  {policy.name}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
              {networkPolicyRef
                ? `Egress filtered via Squid proxy — only allowed hosts will be reachable.`
                : "No policy — VM has unrestricted internet access."}
            </Text>
          </div>

          {/* Storage — hidden when restoring from snapshot (volume size is fixed by the snapshot) */}
          {sourceMode === "fresh" && (
            <div className="instance-selection">
              <Space>
                <DatabaseOutlined />
                <Text strong>Storage</Text>
              </Space>
              <div style={{ marginTop: 8 }}>
                <InputNumber
                  min={60}
                  max={maxStorageGB}
                  step={10}
                  value={storageGB}
                  onChange={(v) => setStorageGB(v ?? 60)}
                  addonAfter="GiB"
                  style={{ width: 180 }}
                  disabled={isLaunching}
                />
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  min 60 GiB · max {maxStorageGB} GiB per desktop
                  {storageUnlimited ? " · org quota: unlimited" : " available"}
                </Text>
              </div>
            </div>
          )}

          {/* Features */}
          <div className="features-grid">
            <div className="feature-item">
              <Text strong>GPU</Text>
              <Text type="secondary">{gpuLabel}</Text>
            </div>
            <div className="feature-item">
              <Text strong>Remote Access</Text>
              <Text type="secondary">NICE DCV</Text>
            </div>
            <div className="feature-item">
              <Text strong>OS</Text>
              <Text type="secondary">Ubuntu 22.04 LTS</Text>
            </div>
          </div>

          {/* Launch Button */}
          <Button
            type="primary"
            size="large"
            icon={isLaunching ? <Spin size="small" /> : <RocketOutlined />}
            onClick={handleLaunch}
            disabled={
              isLaunching ||
              !instanceType ||
              (sourceMode === "fresh" && (!storageGB || storageGB < 60)) ||
              (sourceMode === "snapshot" && !selectedSnapshot)
            }
            block
          >
            {isLaunching
              ? "Launching..."
              : sourceMode === "snapshot"
              ? "Restore & Launch Desktop"
              : "Launch Linux Desktop"}
          </Button>

          {isLaunching && (
            <Text type="secondary" style={{ textAlign: "center", display: "block" }}>
              {sourceMode === "snapshot"
                ? "Restoring from snapshot — VM boots in ~2–3 min."
                : "This may take 2–3 minutes. You will be redirected automatically."}
            </Text>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default LinuxDesktopLauncher;
